<template>
	<div v-if="reports" class="reporting-wrapper">
		<div class="report-dashboard">
			<h3 class="report-type-title">{{ $t('reportType') }}</h3>
			<div class="dashboard-cnt">
				<div class="report-type-filter">
					<div
						v-for="(report, reportName) in reports"
						:key="reportName"
						class="radio-btn-wrapper"
					>
						<ja-multiselect-circle
							:value="selectedReport"
							:selected="selectedReport == reportName"
							:label="$t(reportName)"
							@keyup.enter.native="selectReport(reportName)"
							@click.native="selectReport(reportName)"
						></ja-multiselect-circle>
					</div>
				</div>
				<div>
					<h3>{{ $t('filters') }}</h3>
					<div v-if="selectedReport">
						<ja-simulation-evaluation-form
							v-if="selectedReport == 'simulationEvaluation'"
							:filters="activeFilters"
							:simulation-types="simulationTypes"
							:educator-name="educatorName"
							:clear="clearForm"
							@toggleButton="toggleButton"
							@filtersUpdated="onFiltersUpdated"
						></ja-simulation-evaluation-form>
						<ja-student-summary-form
							v-if="selectedReport == 'studentSummary'"
							:filters="activeFilters"
							:simulation-types="simulationTypes"
							:life-stages="lifeStages"
							:educator-name="educatorName"
							:clear="clearForm"
							@toggleButton="toggleButton"
							@filtersUpdated="onFiltersUpdated"
						></ja-student-summary-form>
						<div class="button-wrapper">
							<ja-button
								class="small"
								name="generateReport"
								:disabled="buttonDisabled"
								@click.native="generateReport"
								>{{
									selectedReport == 'simulationEvaluation'
										? $t('downloadReport')
										: $t('generateReport')
								}}</ja-button
							>
							<ja-button
								class="small outlined"
								name="clearFilters"
								:disabled="clearForm"
								@click.native="clearFilters"
								>{{ $t('clearFilters') }}</ja-button
							>
						</div>
					</div>
					<div v-else class="choose-report-type">
						{{ $t('chooseReportTypeTxt') }}
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
<script>
import gql from 'graphql-tag';
import Button from '../../ui/Button';
import AdminFacility from '@/mixins/AdminFacility';
import MultiselectCircle from '@/components/ui/MultiselectCircle';
import SimulationEvaluationForm from '../forms/SimulationEvaluationForm';
import StudentSummaryForm from '../forms/StudentSummaryForm';
import axios from 'axios';
import { setStorageData, getStorageData } from '@/helpers/storageFunctions';
import { convertDateTimeToUTC } from '@/helpers/utils';

export default {
	components: {
		jaButton: Button,
		jaMultiselectCircle: MultiselectCircle,
		jaSimulationEvaluationForm: SimulationEvaluationForm,
		jaStudentSummaryForm: StudentSummaryForm
	},
	mixins: [AdminFacility],

	data() {
		return {
			activeFilters: null,
			selectedReport: getStorageData('selectedReport') || null,
			educatorName: null,
			lifeStages: [],
			simulationTypes: [],
			educatorID: '5',
			seeAllFacilitiesRoleId: ['1', '2'], //JA Super Admin,JA National/USA Admin
			school: '',
			buttonDisabled: true,
			clearForm: false
		};
	},
	computed: {
		constants() {
			return this.$store.getters.constants;
		},
		reports() {
			if (this.simulationTypes.length && this.lifeStages.length) {
				const reports = {
					studentSummary: {},
					simulationEvaluation: {}
				};

				return reports;
			}

			return null;
		}
	},
	watch: {
		selectedFacility() {
			this.$store.commit('setLoading', true);
			this.clearFilters();
			this.$store.commit('setLoading', false);
		},
		reports(value) {
			if (value) {
				this.$store.commit('setLoading', false);
				if (this.simulationTypes.length && this.lifeStages.length) {
					if (this.selectedReport && sessionStorage.getItem('reportFilters')) {
						this.activeFilters = JSON.parse(
							sessionStorage.getItem('reportFilters')
						);
					}
				}
			}
		},
		buttonDisabled(value) {
			if (value === false) {
				this.clearForm = false;
			}
		}
	},
	mounted() {
		this.$store.commit('setLoading', true);
		this.getSimulationTypes();
		if (this.userData.roleId == this.educatorID) {
			this.educatorName = `${this.userData.firstName} ${this.userData.lastName}`;
		}
	},
	methods: {
		clearFilters() {
			sessionStorage.removeItem('reportFilters');
			sessionStorage.removeItem('selectedReport');
			this.buttonDisabled = true;
			this.activeFilters = null;
			this.clearForm = true;
		},
		onFiltersUpdated(filters) {
			this.activeFilters = filters;
		},
		toggleButton(isEnabled) {
			this.buttonDisabled = !isEnabled;
		},
		generateReport() {
			if (this.activeFilters) {
				if (this.selectedReport == 'simulationEvaluation') {
					this.downloadSimulationEvaluationReport();
				} else {
					setStorageData('selectedReport', this.selectedReport);
					setStorageData('reportFilters', this.activeFilters);
					this.$router.push({ name: this.selectedReport });
				}
			}
		},
		downloadSimulationEvaluationReport() {
			this.$store.commit('setLoading', true);
			const params = {
				facility_ID: this.selectedFacilityId,
				simulationType: this.activeFilters.simulationType,
				schoolName: this.activeFilters.school,
				educatorName: this.activeFilters.educator,
				endDate: convertDateTimeToUTC(this.activeFilters.endDate),
				startDate: convertDateTimeToUTC(this.activeFilters.startDate)
			};

			axios({
				url: '/api/Report/SimulationReportDownload',
				method: 'GET',
				reponseType: 'blob',
				params: params,
				withCredentials: true
			}).then(response => {
				if (response.data.errors && response.data.errors.length > 0) {
					this.$store.commit('setLoading', false);
					this.$store.commit('setErrorMessages', response.data.errors);
					return;
				}
				const url = window.URL.createObjectURL(new Blob([response.data]));
				const link = document.createElement('a');
				link.href = url;

				const currentDate = this.getCurrentDate();
				link.setAttribute(
					'download',
					`SimulationEvaluationReport_${currentDate}.csv`
				);
				document.body.appendChild(link);
				link.click();
				this.$store.commit('setLoading', false);
			});
		},
		getCurrentDate() {
			const now = Date.now();
			const year = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(
				now
			);
			const month = new Intl.DateTimeFormat('en', { month: '2-digit' }).format(
				now
			);
			const date = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(
				now
			);

			const result = `${month}${date}${year}`;
			return result;
		},
		selectReport(report) {
			this.clearFilters();
			this.selectedReport = report;
		},
		async getSimulationTypes() {
			const response = await this.$apollo.query({
				query: gql`
					{
						enumData: enumData(enumType: "simulationType") {
							apiName
							userFriendlyName
							enumIntValue
						}
					}
				`,
				fetchPolicy: 'no-cache'
			});

			if (response.data && response.data.enumData) {
				const simulationTypes = [];
				response.data.enumData.forEach(type => {
					simulationTypes.push({
						name: type.userFriendlyName,
						value: type.apiName
					});
				});
				this.simulationTypes = simulationTypes;
			}
		}
	},
	apollo: {
		lifeStages: {
			query: gql`
				{
					cmsLifeStages(order: "displayOrder") {
						lifeStage_ID
						name
					}
				}
			`,
			fetchPolicy: 'no-cache',
			update: function(data) {
				const lifeStages = [];

				data.cmsLifeStages.forEach(element => {
					lifeStages.push({
						value: element.lifeStage_ID,
						name: element.name
					});
				});

				return lifeStages;
			}
		}
	}
};
</script>
<style lang="scss" scoped>
.reporting-wrapper {
	margin: 32px auto 84px auto;
	max-width: 1000px;

	.report-dashboard {
		width: 100%;

		h3 {
			margin: 16px 0 8px 16px;
		}

		.report-type-title {
			margin: 16px 0 8px 0;
		}

		.dashboard-cnt {
			box-shadow: 0 15px 30px 0 rgba(0, 76, 208, 0.24);
			background-color: $container-bgcolor;
			border: 1px solid $container-border;

			.report-type-filter {
				display: flex;
				flex-wrap: wrap;
				padding: 24px 32px 32px 32px;
				border-bottom: 1px solid $container-border;

				.radio-btn-wrapper {
					width: 50%;
					display: flex;
					align-items: center;
					margin-top: 8px;

					&.disabled {
						.body-text-1 {
							color: $disabled-state;
						}
						* {
							&:hover {
								cursor: not-allowed;
							}
						}
					}
					&:not(.disabled) {
						* {
							&:hover {
								cursor: pointer;
							}
						}
					}
				}
			}
			.button-wrapper {
				display: flex;
				justify-content: center;
				margin: 16px auto;

				:first-child {
					margin-right: 16px;
				}
			}

			.choose-report-type {
				height: 16px;
				min-width: 271px;
				color: $font-dark;
				font-family: 'RobotoRegular';
				font-size: 12px;
				letter-spacing: 1px;
				line-height: 16px;
				text-transform: uppercase;
				text-align: center;
				margin-top: 17px;
				margin-bottom: 35px;

				@media (min-width: $breakpoint-mid) {
					font-size: 15px;
					letter-spacing: 0.5px;
				}
			}
		}
	}
}
</style>
